import type { FormEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { AssignedAdminsStack } from '../components/AssignedAdminsStack';
import { ContextActionsMenu } from '../components/ContextActionsMenu';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { PatientAvatarImage } from '../components/PatientAvatarImage';
import { PermissionGate } from '../components/PermissionGate';
import { SearchBar } from '../components/SearchBar';
import { StatusChip } from '../components/StatusChip';
import {
  Badge,
  Button,
  DataTable,
  Input,
  Modal,
  PaginationFooter,
  SectionHeader,
  Skeleton,
  type DataTableColumn,
} from '../components/ui';
import { useAdminRoster } from '../features/chats/hooks/useAdminRoster';
import { PatientFunnelBoard } from '../features/patients/components/funnel/PatientFunnelBoard';
import { WearingBoard } from '../features/patients/components/WearingBoard';
import { currentAdminProfileId, wearingResultUrl } from '../features/patients/wearingView';
import { useAuth } from '../features/auth/AuthProvider';
import { useHasPermission } from '../features/auth/usePermission';
import type { FunnelFilters } from '../features/patients/hooks/useFunnelColumn';
import { useCreatePatient, useDeletePatient } from '../features/patients/hooks/usePatientMutations';
import { usePatients } from '../features/patients/hooks/usePatients';
import type {
  ChatAdminSummary,
  Patient,
  PatientCreatedResponse,
  StageKind,
  TreatmentStatus,
} from '../types/admin';
import styles from './ClientListPage.module.scss';

const rowsPerPageOptions = [5, 10, 25] as const;

const STAGE_KINDS: readonly StageKind[] = [
  'preparing',
  'modeling',
  'payment',
  'manufacturing_delivery',
  'wearing_aligners',
];

function isStageKind(value: string | null): value is StageKind {
  return value !== null && (STAGE_KINDS as readonly string[]).includes(value);
}

function parseCycleNumber(value: string | null): number | null {
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function RegistrationCodePill({ code }: { code: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleClick(event: ReactMouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={styles.pendingCode}
      onClick={handleClick}
      title={copied ? t('clients.copied') : t('clients.copy')}
      aria-label={t('clients.copy')}
    >
      {copied ? t('clients.copied') : code}
    </button>
  );
}

export function ClientListPage() {
  const { user } = useAuth();
  const canViewWearing = useHasPermission('photo_controls:view');
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [createdPatient, setCreatedPatient] = useState<PatientCreatedResponse | null>(null);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    readonly patient: Patient;
    readonly x: number;
    readonly y: number;
  } | null>(null);
  const createPatientMutation = useCreatePatient();
  const deletePatientMutation = useDeletePatient();
  const adminRosterQuery = useAdminRoster();
  const search = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const filterOptions: { label: string; value: TreatmentStatus | null }[] = [
    { label: t('clients.all'), value: null },
    { label: t('status.active'), value: 'active' },
    { label: t('status.draft'), value: 'draft' },
    { label: t('status.completed'), value: 'completed' },
    { label: t('status.cancelled'), value: 'cancelled' },
  ];
  const statusFilter = filterOptions.some((option) => option.value === statusParam)
    ? (statusParam as TreatmentStatus)
    : null;
  const page = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) - 1 || 0, 0);
  const rowsPerPage = rowsPerPageOptions.includes(Number.parseInt(searchParams.get('pageSize') ?? '10', 10) as 5 | 10 | 25)
    ? (Number.parseInt(searchParams.get('pageSize') ?? '10', 10) as 5 | 10 | 25)
    : 10;
  const mineOnly = searchParams.get('mine') !== '0';
  const assignedAdminIds = useMemo(
    () => searchParams.getAll('assigned_admin_ids'),
    [searchParams],
  );
  const stageParam = searchParams.get('current_stage_kind');
  const currentStageKind: StageKind | null = isStageKind(stageParam) ? stageParam : null;
  const cycleParam = searchParams.get('current_cycle_number');
  const currentCycleNumber: number | null = parseCycleNumber(cycleParam);
  const view = searchParams.get('view') === 'funnel' ? 'funnel' : searchParams.get('view') === 'wearing' && canViewWearing ? 'wearing' : 'table';

  const { data, error, isError, isLoading } = usePatients({
    search: search || undefined,
    treatment_status: statusFilter,
    page: page + 1,
    page_size: rowsPerPage,
    mine: mineOnly,
    assigned_admin_ids: assignedAdminIds.length > 0 ? assignedAdminIds : undefined,
    // The funnel board breaks patients out by step itself, so the global step
    // filter must not constrain the totals shown in the page subtitle.
    current_stage_kind: view !== 'table' ? null : currentStageKind,
    current_cycle_number: currentCycleNumber,
  });

  const funnelFilters = useMemo<FunnelFilters>(
    () => ({
      search: search || undefined,
      treatment_status: statusFilter,
      mine: mineOnly,
      assigned_admin_ids: assignedAdminIds.length > 0 ? assignedAdminIds : undefined,
      current_cycle_number: currentCycleNumber,
    }),
    [search, statusFilter, mineOnly, assignedAdminIds, currentCycleNumber],
  );

  const patients = data?.items ?? [];
  const total = data?.total ?? 0;
  const adminRoster = adminRosterQuery.data ?? [];
  const currentAdminId = currentAdminProfileId(adminRoster, user?.id);
  const adminById = useMemo(() => {
    const map = new Map<string, ChatAdminSummary>();
    for (const admin of adminRoster) {
      map.set(admin.admin_id, admin);
    }
    return map;
  }, [adminRoster]);

  const activeFiltersLabel = useMemo(() => {
    const labels: string[] = [];
    if (search) labels.push(t('clients.searchTag', { value: search }));
    if (statusFilter) {
      labels.push(t('clients.statusTag', { value: t(`status.${statusFilter}`) }));
    }
    for (const adminId of assignedAdminIds) {
      const admin = adminById.get(adminId);
      labels.push(t('clients.adminTag', { value: admin?.full_name ?? adminId }));
    }
    if (currentStageKind) {
      labels.push(t('clients.stepTag', { value: t(`stages.${currentStageKind}`) }));
    }
    if (currentCycleNumber !== null) {
      labels.push(t('clients.cycleTag', { value: String(currentCycleNumber) }));
    }
    return labels;
  }, [
    search,
    statusFilter,
    assignedAdminIds,
    adminById,
    currentStageKind,
    currentCycleNumber,
    t,
  ]);

  const activeFilterCount =
    assignedAdminIds.length +
    (currentStageKind ? 1 : 0) +
    (currentCycleNumber !== null ? 1 : 0);

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function updateSearchParams(
    next: Partial<{
      q: string;
      status: TreatmentStatus | null;
      page: number;
      pageSize: number;
      mine: boolean;
      assigned_admin_ids: readonly string[];
      current_stage_kind: StageKind | null;
      current_cycle_number: number | null;
      view: 'table' | 'funnel' | 'wearing';
    }>,
    replace = false,
  ) {
    const updated = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      next.q ? updated.set('q', next.q) : updated.delete('q');
    }
    if (next.status !== undefined) {
      next.status ? updated.set('status', next.status) : updated.delete('status');
    }
    if (next.page !== undefined) {
      next.page > 1 ? updated.set('page', String(next.page)) : updated.delete('page');
    }
    if (next.pageSize !== undefined) {
      next.pageSize !== 10 ? updated.set('pageSize', String(next.pageSize)) : updated.delete('pageSize');
    }
    if (next.mine !== undefined) {
      next.mine ? updated.delete('mine') : updated.set('mine', '0');
    }
    if (next.assigned_admin_ids !== undefined) {
      updated.delete('assigned_admin_ids');
      for (const adminId of next.assigned_admin_ids) {
        updated.append('assigned_admin_ids', adminId);
      }
    }
    if (next.current_stage_kind !== undefined) {
      next.current_stage_kind
        ? updated.set('current_stage_kind', next.current_stage_kind)
        : updated.delete('current_stage_kind');
    }
    if (next.current_cycle_number !== undefined) {
      next.current_cycle_number !== null
        ? updated.set('current_cycle_number', String(next.current_cycle_number))
        : updated.delete('current_cycle_number');
    }
    if (next.view !== undefined) {
      next.view !== 'table' ? updated.set('view', next.view) : updated.delete('view');
    }

    setSearchParams(updated, { replace });
  }

  const listUrlSuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const columns: DataTableColumn<Patient>[] = [
    {
      key: 'patient',
      header: t('clients.patient'),
      render: (patient) => (
        <div className={styles.patientCell}>
          <div className={styles.avatar}>
            {patient.avatar_url ? (
              <PatientAvatarImage
                patientId={patient.id}
                avatarUrl={patient.avatar_url}
                alt=""
                fallback={
                  <>
                    {patient.first_name[0]}
                    {patient.last_name[0]}
                  </>
                }
              />
            ) : (
              <>
                {patient.first_name[0]}
                {patient.last_name[0]}
              </>
            )}
          </div>
          <div className={styles.patientText}>
            <span className={styles.patientName}>
              {patient.first_name} {patient.last_name}
            </span>
            <span className={styles.patientMeta}>
              {patient.display_id ?? patient.registration_code ?? t('clients.noClientId')}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'id',
      header: 'ID',
      render: (patient) => (
        <span className={styles.monoText}>
          {patient.display_id ?? patient.registration_code ?? '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('clients.status'),
      render: (patient) =>
        patient.active_treatment_status ? (
          <StatusChip status={patient.active_treatment_status} />
        ) : (
          <span className={styles.secondaryText}>—</span>
        ),
    },
    {
      key: 'assigned',
      header: t('clients.assignedAdmins'),
      className: styles.hideOnMobile,
      render: (patient) => <AssignedAdminsStack admins={patient.assigned_admins ?? []} />,
    },
    {
      key: 'account',
      header: t('clients.account'),
      className: styles.hideOnMobile,
      render: (patient) =>
        patient.registration_pending ? (
          <div className={styles.accountStack}>
            <Badge variant="warning">{t('clients.pendingSignup')}</Badge>
            {patient.registration_code && (
              <RegistrationCodePill code={patient.registration_code} />
            )}
          </div>
        ) : patient.is_active ? (
          <Badge variant="success" dot>
            {t('clients.active')}
          </Badge>
        ) : (
          <Badge variant="neutral">{t('clients.inactive')}</Badge>
        ),
    },
    {
      key: 'email',
      header: t('clients.email'),
      className: styles.hideOnMobile,
      render: (patient) => <span className={styles.secondaryText}>{patient.email ?? '—'}</span>,
    },
    {
      key: 'updated',
      header: t('clients.updated'),
      className: styles.hideOnTablet,
      render: (patient) => <span className={styles.secondaryText}>{formatDate(patient.updated_at)}</span>,
    },
    {
      key: 'actions',
      header: t('clients.actions'),
      align: 'right',
      className: styles.actionsColumn,
      render: (patient) => (
        <RowActionsMenu
          onChat={() => navigate(`/chats?patient=${patient.id}`)}
          onDelete={() => setPatientToDelete(patient)}
        />
      ),
    },
  ];

  const deleteErrorMessage =
    deletePatientMutation.error instanceof Error
      ? deletePatientMutation.error.message
      : deletePatientMutation.error
        ? t('clients.deleteFailed')
        : null;

  function closeDeleteModal() {
    if (deletePatientMutation.isPending) {
      return;
    }
    setPatientToDelete(null);
    deletePatientMutation.reset();
  }

  async function handleConfirmDelete() {
    if (!patientToDelete) {
      return;
    }
    try {
      await deletePatientMutation.mutateAsync(patientToDelete.id);
      setPatientToDelete(null);
      deletePatientMutation.reset();
    } catch {
      // Error surfaced via deletePatientMutation.error
    }
  }

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader
          title={t('clients.title')}
          subtitle={t('clients.totalPatients', { count: total })}
          actions={
            <Button
              variant="primary"
              leadingIcon={<Icon name="person-add" size={16} />}
              onClick={() => {
                setCreatedPatient(null);
                setIsCreateOpen(true);
              }}
            >
              {t('clients.addClient')}
            </Button>
          }
        />

        <div className={styles.filters}>
          <SearchBar
            value={search}
            onChange={(value) => {
              updateSearchParams({ q: value, page: 1 }, true);
            }}
            placeholder={t('clients.searchPlaceholder')}
          />
          <div className={styles.filtersInline}>
            <button
              type="button"
              className={`${styles.filterButton} ${activeFilterCount > 0 ? styles.filterButtonActive : ''}`}
              onClick={() => setIsFiltersOpen(true)}
              aria-label={t('clients.filters')}
            >
              <Icon name="filter" size={16} />
              <span>{t('clients.filters')}</span>
              {activeFilterCount > 0 && (
                <span className={styles.filterBadge}>{activeFilterCount}</span>
              )}
            </button>
            <div className={styles.scopeToggle} role="tablist" aria-label={t('clients.scopeMine')}>
              <button
                type="button"
                role="tab"
                aria-selected={mineOnly}
                className={`${styles.scopeButton} ${mineOnly ? styles.scopeButtonActive : ''}`}
                onClick={() => updateSearchParams({ mine: true, page: 1 })}
              >
                {t('clients.scopeMine')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!mineOnly}
                className={`${styles.scopeButton} ${!mineOnly ? styles.scopeButtonActive : ''}`}
                onClick={() => updateSearchParams({ mine: false, page: 1 })}
              >
                {t('clients.scopeAll')}
              </button>
            </div>
            <div className={styles.scopeToggle} role="tablist" aria-label={t('clients.viewFunnel')}>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'table'}
                className={`${styles.scopeButton} ${view === 'table' ? styles.scopeButtonActive : ''}`}
                onClick={() => updateSearchParams({ view: 'table' })}
              >
                {t('clients.viewTable')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'funnel'}
                className={`${styles.scopeButton} ${view === 'funnel' ? styles.scopeButtonActive : ''}`}
                onClick={() => updateSearchParams({ view: 'funnel' })}
              >
                {t('clients.viewFunnel')}
              </button>
              {canViewWearing && <button type="button" role="tab" aria-selected={view === 'wearing'} className={`${styles.scopeButton} ${view === 'wearing' ? styles.scopeButtonActive : ''}`} onClick={() => updateSearchParams({ view: 'wearing' })}>{i18n.language.startsWith('en') ? 'Wearing' : 'Носіння кап'}</button>}
            </div>
          </div>
        </div>

        {activeFiltersLabel.length > 0 && (
          <div className={styles.activeFilters}>
            {activeFiltersLabel.map((label) => (
              <span key={label} className={styles.activeFilterTag}>
                {label}
              </span>
            ))}
          </div>
        )}

        {view === 'wearing' ? (
          <WearingBoard
            managers={adminRoster.map((admin) => ({ id: admin.admin_id, name: admin.full_name }))}
            selectedManagerIds={assignedAdminIds}
            mineOnly={mineOnly}
            currentAdminId={currentAdminId}
            search={search}
            onOpen={(patientId, cycleId) => navigate(wearingResultUrl(patientId, cycleId, searchParams))}
            onChat={(patientId) => navigate(`/chats?patient=${patientId}`)}
          />
        ) : view === 'funnel' ? (
          <PatientFunnelBoard
            filters={funnelFilters}
            onOpenPatient={(patientId) => navigate(`/clients/${patientId}${listUrlSuffix}`)}
            onChatPatient={(patientId) => navigate(`/chats?patient=${patientId}`)}
            onRequestDelete={(patient) => setPatientToDelete(patient)}
          />
        ) : isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} width="100%" height={56} />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            title={t('clients.loadErrorTitle')}
            description={error instanceof Error ? error.message : t('clients.loadErrorDescription')}
          />
        ) : patients.length === 0 ? (
          <EmptyState
            title={
              search
                ? t('clients.emptyTitle')
                : mineOnly
                  ? t('clients.emptyMineTitle')
                  : t('clients.emptyTitle')
            }
            description={
              search
                ? t('clients.emptySearch')
                : mineOnly
                  ? t('clients.emptyMineDescription')
                  : t('clients.emptyDefault')
            }
          />
        ) : (
          <DataTable<Patient>
            columns={columns}
            rows={patients}
            rowKey={(patient) => patient.id}
            onRowDoubleClick={(patient) => navigate(`/clients/${patient.id}${listUrlSuffix}`)}
            onRowContextMenu={(patient, event) => {
              event.preventDefault();
              setContextMenu({ patient, x: event.clientX, y: event.clientY });
            }}
            footer={
              <PaginationFooter
                page={page + 1}
                pageSize={rowsPerPage}
                total={total}
                pageSizeOptions={[...rowsPerPageOptions]}
                onPageChange={(nextPage) => updateSearchParams({ page: nextPage })}
                onPageSizeChange={(size) => updateSearchParams({ pageSize: size, page: 1 })}
                labels={{
                  rowsPerPage: t('clients.rowsPerPage'),
                  rangeOf: (from, to, totalCount) =>
                    t('clients.rangeOfTotal', { from, to, total: totalCount }),
                  previous: t('clients.previousPage'),
                  next: t('clients.nextPage'),
                }}
              />
            }
          />
        )}
      </div>

      <CreateClientModal
        open={isCreateOpen}
        createdPatient={createdPatient}
        isSubmitting={createPatientMutation.isPending}
        errorMessage={createPatientMutation.error instanceof Error ? createPatientMutation.error.message : null}
        onClose={() => {
          if (!createPatientMutation.isPending) {
            setIsCreateOpen(false);
            setCreatedPatient(null);
            createPatientMutation.reset();
          }
        }}
        onSubmit={async (payload) => {
          const result = await createPatientMutation.mutateAsync(payload);
          setCreatedPatient(result);
        }}
        onOpenClient={(patientId) => {
          setIsCreateOpen(false);
          setCreatedPatient(null);
          createPatientMutation.reset();
          navigate(`/clients/${patientId}${listUrlSuffix}`);
        }}
      />

      <Modal
        open={patientToDelete !== null}
        onClose={closeDeleteModal}
        title={t('clients.deleteClientTitle')}
        subtitle={t('clients.deleteClientConfirm', {
          name: `${patientToDelete?.first_name ?? ''} ${patientToDelete?.last_name ?? ''}`.trim(),
        })}
        size="sm"
        closeOnBackdrop={!deletePatientMutation.isPending}
        closeAriaLabel={t('clients.cancel')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeDeleteModal}
              disabled={deletePatientMutation.isPending}
            >
              {t('clients.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleConfirmDelete()}
              disabled={deletePatientMutation.isPending || patientToDelete === null}
            >
              {deletePatientMutation.isPending ? t('clients.deleting') : t('clients.delete')}
            </Button>
          </>
        }
      >
        {deleteErrorMessage ? <p className={styles.formError}>{deleteErrorMessage}</p> : null}
      </Modal>

      {contextMenu && (
        <ContextActionsMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => navigate(`/clients/${contextMenu.patient.id}${listUrlSuffix}`)}
          onChat={() => navigate(`/chats?patient=${contextMenu.patient.id}`)}
          onDelete={() => setPatientToDelete(contextMenu.patient)}
        />
      )}

      <FiltersModal
        open={isFiltersOpen}
        admins={adminRoster}
        adminsLoading={adminRosterQuery.isLoading}
        selectedAdminIds={assignedAdminIds}
        selectedStep={currentStageKind}
        selectedCycle={currentCycleNumber}
        hideStepFilter={view === 'funnel'}
        onClose={() => setIsFiltersOpen(false)}
        onApply={(applied) => {
          updateSearchParams({
            assigned_admin_ids: applied.adminIds,
            current_stage_kind: applied.step,
            current_cycle_number: applied.cycle,
            page: 1,
          });
          setIsFiltersOpen(false);
        }}
      />
    </AppShell>
  );
}

type RowActionsMenuProps = {
  readonly onChat: () => void;
  readonly onDelete: () => void;
};

const ROW_MENU_GAP = 4;
const ROW_MENU_VIEWPORT_MARGIN = 8;

/**
 * The table clips its own overflow (`DataTable` wraps the rows in an
 * `overflow: hidden` card around an `overflow-x: auto` scroller), so an
 * absolutely positioned dropdown on the last row is cut off and forces the
 * scroller to grow. The menu is therefore portalled to `document.body` and
 * positioned `fixed` against the trigger, flipping above it when there is no
 * room below.
 */
function RowActionsMenu({ onChat, onDelete }: RowActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }
    function handleDocumentClick(event: MouseEvent) {
      const target = event.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target) ?? false;
      const insideMenu = menuRef.current?.contains(target) ?? false;
      if (!insideWrapper && !insideMenu) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    function close() {
      setOpen(false);
    }
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKey);
    // A fixed menu no longer follows its row, so any scroll or resize closes it.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) {
      return;
    }
    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - trigger.bottom - ROW_MENU_GAP;
    const spaceAbove = trigger.top - ROW_MENU_GAP;
    const flipUp = spaceBelow < menu.height + ROW_MENU_VIEWPORT_MARGIN && spaceAbove > spaceBelow;
    const maxLeft = window.innerWidth - menu.width - ROW_MENU_VIEWPORT_MARGIN;
    setMenuPosition({
      top: flipUp ? trigger.top - ROW_MENU_GAP - menu.height : trigger.bottom + ROW_MENU_GAP,
      left: Math.max(ROW_MENU_VIEWPORT_MARGIN, Math.min(trigger.right - menu.width, maxLeft)),
    });
  }, [open]);

  function runAction(action: () => void) {
    setOpen(false);
    action();
  }

  return (
    <div
      ref={wrapperRef}
      className={styles.rowMenuWrapper}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.rowMenuTrigger} ${open ? styles.rowMenuTriggerOpen : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('clients.moreActions')}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="more-vert" size={18} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.rowMenu}
            role="menu"
            // Hidden for the first paint: the menu has to be laid out before it
            // can be measured and placed.
            style={
              menuPosition
                ? { top: menuPosition.top, left: menuPosition.left }
                : { top: 0, left: 0, visibility: 'hidden' }
            }
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              className={styles.rowMenuItem}
              onClick={() => runAction(onChat)}
            >
              {t('clients.chat')}
            </button>
            <PermissionGate permission="patients:delete">
              <button
                type="button"
                role="menuitem"
                className={`${styles.rowMenuItem} ${styles.rowMenuItemDanger}`}
                onClick={() => runAction(onDelete)}
              >
                {t('clients.delete')}
              </button>
            </PermissionGate>
          </div>,
          document.body,
        )}
    </div>
  );
}

type AppliedFilters = {
  readonly adminIds: readonly string[];
  readonly step: StageKind | null;
  readonly cycle: number | null;
};

type FiltersModalProps = {
  readonly open: boolean;
  readonly admins: readonly ChatAdminSummary[];
  readonly adminsLoading: boolean;
  readonly selectedAdminIds: readonly string[];
  readonly selectedStep: StageKind | null;
  readonly selectedCycle: number | null;
  readonly hideStepFilter?: boolean;
  readonly onClose: () => void;
  readonly onApply: (filters: AppliedFilters) => void;
};

function FiltersModal({
  open,
  admins,
  adminsLoading,
  selectedAdminIds,
  selectedStep,
  selectedCycle,
  hideStepFilter = false,
  onClose,
  onApply,
}: FiltersModalProps) {
  const { t } = useTranslation();
  const adminPickerId = useId();
  const adminPickerRef = useRef<HTMLDivElement>(null);
  const adminPickerTriggerRef = useRef<HTMLButtonElement>(null);
  const adminSearchInputRef = useRef<HTMLInputElement>(null);
  const [draftAdminIds, setDraftAdminIds] = useState<readonly string[]>(selectedAdminIds);
  const [draftStep, setDraftStep] = useState<StageKind | null>(selectedStep);
  const [draftCycle, setDraftCycle] = useState<string>(
    selectedCycle !== null ? String(selectedCycle) : '',
  );
  const [adminPickerOpen, setAdminPickerOpen] = useState(false);
  const [adminSearch, setAdminSearch] = useState('');

  const filteredAdmins = useMemo(() => {
    const query = adminSearch.trim().toLocaleLowerCase();
    if (!query) return admins;
    return admins.filter((admin) => admin.full_name.toLocaleLowerCase().includes(query));
  }, [adminSearch, admins]);

  const selectedAdminLabel = useMemo(() => {
    if (draftAdminIds.length === 0) return t('clients.filterByAdmin');
    const adminNames = new Map(admins.map((admin) => [admin.admin_id, admin.full_name]));
    return draftAdminIds.map((adminId) => adminNames.get(adminId) ?? adminId).join(', ');
  }, [admins, draftAdminIds, t]);

  useEffect(() => {
    if (open) {
      setDraftAdminIds(selectedAdminIds);
      setDraftStep(selectedStep);
      setDraftCycle(selectedCycle !== null ? String(selectedCycle) : '');
      setAdminPickerOpen(false);
      setAdminSearch('');
    }
  }, [
    open,
    selectedAdminIds,
    selectedStep,
    selectedCycle,
  ]);

  useEffect(() => {
    if (!adminPickerOpen) return undefined;

    adminSearchInputRef.current?.focus();

    function handlePointerDown(event: PointerEvent) {
      if (event.target instanceof Node && !adminPickerRef.current?.contains(event.target)) {
        setAdminPickerOpen(false);
        setAdminSearch('');
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [adminPickerOpen]);

  function closeAdminPicker({ restoreFocus = false } = {}) {
    setAdminPickerOpen(false);
    setAdminSearch('');
    if (restoreFocus) {
      adminPickerTriggerRef.current?.focus();
    }
  }

  function toggleAdmin(adminId: string) {
    setDraftAdminIds((current) =>
      current.includes(adminId)
        ? current.filter((id) => id !== adminId)
        : [...current, adminId],
    );
  }

  function clearAll() {
    setDraftAdminIds([]);
    setDraftStep(null);
    setDraftCycle('');
    closeAdminPicker();
  }

  function handleApply() {
    const parsedCycle = parseCycleNumber(draftCycle === '' ? null : draftCycle);
    onApply({
      adminIds: draftAdminIds,
      step: draftStep,
      cycle: parsedCycle,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('clients.filters')}
      size="md"
      closeAriaLabel={t('clients.close')}
      footer={
        <>
          <Button variant="secondary" onClick={clearAll}>
            {t('clients.clearAllFilters')}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t('clients.applyFilters')}
          </Button>
        </>
      }
    >
      <div className={styles.filterModalBody}>
        <section className={styles.filterSection}>
          <h4 className={styles.filterSectionTitle}>{t('clients.filterByAdmin')}</h4>
          {adminsLoading ? (
            <Skeleton width="100%" height={120} />
          ) : admins.length === 0 ? (
            <p className={styles.checkboxEmpty}>{t('clients.noAssignedAdmins')}</p>
          ) : (
            <div
              ref={adminPickerRef}
              className={styles.adminPicker}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && adminPickerOpen) {
                  event.preventDefault();
                  event.stopPropagation();
                  closeAdminPicker({ restoreFocus: true });
                }
              }}
            >
              <button
                ref={adminPickerTriggerRef}
                type="button"
                className={`${styles.adminPickerTrigger} ${draftAdminIds.length > 0 ? styles.adminPickerTriggerActive : ''}`}
                aria-expanded={adminPickerOpen}
                aria-controls={adminPickerId}
                onClick={() => {
                  if (adminPickerOpen) {
                    closeAdminPicker();
                  } else {
                    setAdminPickerOpen(true);
                  }
                }}
              >
                <span className={styles.adminPickerLabel}>{selectedAdminLabel}</span>
                <span className={`${styles.adminPickerCaret} ${adminPickerOpen ? styles.adminPickerCaretOpen : ''}`}>
                  <Icon name="chevron-down" size={16} />
                </span>
              </button>

              {adminPickerOpen ? (
                <div id={adminPickerId} className={styles.adminPickerPopover}>
                  <div className={styles.adminPickerSearch}>
                    <Icon name="search" size={16} />
                    <input
                      ref={adminSearchInputRef}
                      type="search"
                      value={adminSearch}
                      onChange={(event) => setAdminSearch(event.target.value)}
                      placeholder={t('clients.searchGeneric')}
                      aria-label={t('clients.searchGeneric')}
                    />
                  </div>
                  <div
                    className={styles.checkboxList}
                    role="group"
                    aria-label={t('clients.filterByAdmin')}
                  >
                    {filteredAdmins.length === 0 ? (
                      <p className={styles.checkboxEmpty}>{t('chats.searchNoResults')}</p>
                    ) : (
                      filteredAdmins.map((admin) => {
                        const checked = draftAdminIds.includes(admin.admin_id);
                        return (
                          <label
                            key={admin.admin_id}
                            className={`${styles.checkboxRow} ${checked ? styles.checkboxRowChecked : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAdmin(admin.admin_id)}
                            />
                            <span>{admin.full_name}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </section>

        {!hideStepFilter && (
          <section className={styles.filterSection}>
            <h4 className={styles.filterSectionTitle}>{t('clients.filterByStep')}</h4>
            <div className={styles.stageChips} role="radiogroup" aria-label={t('clients.filterByStep')}>
              <button
                type="button"
                role="radio"
                aria-checked={draftStep === null}
                className={`${styles.chip} ${draftStep === null ? styles.chipActive : ''}`}
                onClick={() => setDraftStep(null)}
              >
                {t('clients.anyStage')}
              </button>
              {STAGE_KINDS.map((stage) => (
                <button
                  key={stage}
                  type="button"
                  role="radio"
                  aria-checked={draftStep === stage}
                  className={`${styles.chip} ${draftStep === stage ? styles.chipActive : ''}`}
                  onClick={() => setDraftStep(stage)}
                >
                  {t(`stages.${stage}`)}
                </button>
              ))}
            </div>
          </section>
        )}

        <section className={styles.filterSection}>
          <h4 className={styles.filterSectionTitle}>{t('clients.filterByStage')}</h4>
          <Input
            id="filter-current-cycle-number"
            type="number"
            min={1}
            inputMode="numeric"
            value={draftCycle}
            wrapperClassName={styles.cycleInput}
            onChange={(event) => setDraftCycle(event.target.value)}
          />
        </section>

      </div>
    </Modal>
  );
}

type CreateClientFormValues = {
  readonly first_name: string;
  readonly last_name: string;
};

type CreateClientModalProps = {
  readonly open: boolean;
  readonly createdPatient: PatientCreatedResponse | null;
  readonly isSubmitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (payload: CreateClientFormValues) => Promise<void>;
  readonly onOpenClient: (patientId: string) => void;
};

function CreateClientModal({
  open,
  createdPatient,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
  onOpenClient,
}: CreateClientModalProps) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<CreateClientFormValues>({
    first_name: '',
    last_name: '',
  });
  const [copyHint, setCopyHint] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await onSubmit({
        first_name: formState.first_name.trim(),
        last_name: formState.last_name.trim(),
      });
    } catch {
      return;
    }
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopyHint(t('clients.copied'));
      window.setTimeout(() => setCopyHint(null), 2000);
    } catch {
      setCopyHint(t('clients.copyFailed'));
      window.setTimeout(() => setCopyHint(null), 3000);
    }
  }

  const title = createdPatient ? t('clients.inviteCodeReady') : t('clients.createClient');
  const subtitle = createdPatient
    ? t('clients.inviteCodeDescription')
    : t('clients.createClientDescription');

  const footer = createdPatient ? (
    <>
      <Button variant="secondary" onClick={onClose}>
        {t('clients.close')}
      </Button>
      <Button variant="primary" onClick={() => onOpenClient(createdPatient.id)}>
        {t('clients.openClientRecord')}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('clients.cancel')}
      </Button>
      <Button
        type="submit"
        form="create-client-form"
        variant="primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? t('clients.creating') : t('clients.createAndShowCode')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="md"
      closeOnBackdrop={!isSubmitting}
      closeAriaLabel={t('clients.closeCreateClientForm')}
      footer={footer}
    >
      {createdPatient ? (
        <div className={styles.modalForm}>
          <p className={styles.inviteHint}>
            {t('clients.codeForPatient', {
              name: `${createdPatient.first_name} ${createdPatient.last_name}`,
            })}
          </p>
          <div className={styles.inviteCodeBox}>
            <span className={styles.inviteCodeValue} data-testid="registration-code">
              {createdPatient.registration_code}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void copyCode(createdPatient.registration_code)}
            >
              {t('clients.copy')}
            </Button>
          </div>
          {copyHint && <p className={styles.copyHint}>{copyHint}</p>}
        </div>
      ) : (
        <form id="create-client-form" className={styles.modalForm} onSubmit={handleSubmit}>
          <Input
            id="create-client-first-name"
            label={t('clients.firstName')}
            value={formState.first_name}
            onChange={(event) =>
              setFormState((current) => ({ ...current, first_name: event.target.value }))
            }
            required
          />
          <Input
            id="create-client-last-name"
            label={t('clients.lastName')}
            value={formState.last_name}
            onChange={(event) =>
              setFormState((current) => ({ ...current, last_name: event.target.value }))
            }
            required
          />
          {errorMessage && <p className={styles.formError}>{errorMessage}</p>}
        </form>
      )}
    </Modal>
  );
}
